// Define a class for a friendly station

import {Graphics, Sprite} from "pixi.js";
import {Vector} from "./i.js"; // ./vector.js
import {rand, RES} from "./i.js"; // ./dump.js
import {ConfigStructure, OptionalProperty, RequiredProperty} from "./i.js"; // ./validators.js
import {AIUtils} from "./i.js"; // ./ai.js
import {Thing, THING_TYPES, Z_INDICES} from "./i.js"; // ./thing.js

export class Station extends Thing {
	static CONFIG_STRUCTURE = new ConfigStructure([
		// Name of this station
		// Shown to the player via morse code, and in the future in other ways
		// too.
		new RequiredProperty("name", v => (typeof v === "string")),
		// pos of this station
		new RequiredProperty("pos", v => (v instanceof Vector)),
		// vel of this station
		new OptionalProperty("vel", v => (v instanceof Vector)),
		// rot of this station, in radians
		new OptionalProperty("rot", v => (Number.isFinite(v))),
		// Health of this station
		new OptionalProperty("health", v => (Number.isFinite(v) && v >= 0)),
		// The fraction by which the dock is currently visually extended
		new OptionalProperty("extensionFraction", v =>
						(Number.isFinite(v) && v >= 0 && v <= 1))
	]);

	// Note that this map uses dots and dashes, but most other code represents
	// morse code as a string of 0s and 1s encoding lengths of all signals and
	// gaps.
	// Also not that space is handled separately and isn't included in this map.
	static MORSE_CODE = new Map([
		["A", ".-"  ],
		["B", "-..."],
		["C", "-.-."],
		["D", "-.." ],
		["E", "."   ],
		["F", "..-."],
		["G", "--." ],
		["H", "...."],
		["I", ".."  ],
		["J", ".---"],
		["K", "-.-" ],
		["L", ".-.."],
		["M", "--"  ],
		["N", "-."  ],
		["O", "---" ],
		["P", ".--."],
		["Q", "--.-"],
		["R", ".-." ],
		["S", "..." ],
		["T", "-"   ],
		["U", "..-" ],
		["V", "...-"],
		["W", ".--" ],
		["X", "-..-"],
		["Y", "-.--"],
		["Z", "--.."]
	]);

	static SPIN = 0.1;
	static SHIELD_ALPHA_DECAY = 0.6;

	// The duration of the shortest flash of the morse LED, in seconds
	static MORSE_RHYTHM = 0.5;
	// The duration of the flash of the action LED, in seconds
	static ACTION_RHYTHM = 0.5;
	// Time it takes the dock to fully extend / retract, in seconds
	static EXTENSION_TIME = 1;

	// Positions of station elements in texture pixels
	static DOCK_RETRACTED = new Vector(4, 258);
	static DOCK_EXTENDED = new Vector(-45, 258);
	static MORSE_LED_LOCATION = new Vector(60, 204);
	static ACTION_LED_LOCATION = new Vector(60, 224);

	// Create a new station
	// config is an object with the properties provided by the spawn request
	// enrichment, plus the properties specified in CONFIG_STRUCTURE above.
	constructor(config) {
		Station.CONFIG_STRUCTURE.test(config);

		config.zIndex = Z_INDICES.STATION;
		config.len = 2.4;
		if (config.vel === undefined) config.vel = new Vector();
		if (config.rot === undefined) config.rot = rand(0, 2 * Math.PI);
		config.maxHealth = 1;
		config.type = THING_TYPES.STATION;
		config.canDespawn = false;
		config.gpsIcon = "images/icons/gps/station.svg";
		config.showBossBar = true;
		super(config);

		// The fraction by which the dock is currently visually extended
		this._extensionFraction = config.extensionFraction ?? 0;

		this._morseName = Station._morsify(this.name);

		this._body = new Sprite(RES["images/stations/origin.svg"].texture);
		this._body.anchor.set(0.5, 0.5);
		this._body.position.set(0, 0);
		this._body.scale.set((2 / 3) * this.len / this._body.texture.height);

		this._dock = new Sprite(RES["images/stations/dock.svg"].texture);
		this._dock.anchor.set(0, 0.5);
		this._dock.position.copyFrom(
			this._texturePixelsToFlares(Station.DOCK_EXTENDED)
		);
		this._dock.scale.copyFrom(this._body.scale);

		this._morseLed = new Sprite(
			RES["images/stations/morse.svg"].texture
		);
		this._morseLed.anchor.set(0.5, 0.5);
		this._morseLed.position.copyFrom(
			this._texturePixelsToFlares(Station.MORSE_LED_LOCATION)
		);
		this._morseLed.scale.copyFrom(this._body.scale);

		this._actionLed = new Sprite(
			RES["images/stations/action.svg"].texture
		);
		this._actionLed.anchor.set(0.5, 0.5);
		this._actionLed.position.copyFrom(
			this._texturePixelsToFlares(Station.ACTION_LED_LOCATION)
		);
		this._actionLed.scale.copyFrom(this._body.scale);

		this._shield = new Graphics();
		this._shield.beginFill(0x00ff44);
		this._shield.drawCircle(0, 0, this.radius);
		this._shield.endFill();
		this._shield.alpha = 0;

		this.display.addChild(
			this._dock,
			this._body,
			this._morseLed,
			this._actionLed,
			this._shield
		);
	}

	// Rotate the station
	// dt is the time since last update, in seconds. time is the total in-game
	// time since the start of the game, in seconds.
	move(dt, time) {
		// Move the station
		AIUtils.rotateBy(this, Station.SPIN, dt);

		// Fade the shield
		this._shield.alpha = Math.max(
			this._shield.alpha - Station.SHIELD_ALPHA_DECAY * dt,
			0
		);

		// Extend or retract the dock

		// Calculate the fraction by which the dock can extend or retract this
		// tick
		let tick_extension_fraction = dt / Station.EXTENSION_TIME;
		// Figure out whether the dock should aim for being extended or
		// retracted
		let goalIsExtended = (this.space.player.dockingTo === this.id);
		// Adjust the extension fraction with no regard for bounds
		this._extensionFraction += tick_extension_fraction
						* (goalIsExtended ? 1 : -1);
		// Clamp the extension fraction between 0 and 1
		this._extensionFraction = Math.min(Math.max(this._extensionFraction, 0),
						1);
		// Adjust the display to match the extension fraction
		this._dock.position.copyFrom(Vector.interpolate(
			this._texturePixelsToFlares(Station.DOCK_RETRACTED),
			this._texturePixelsToFlares(Station.DOCK_EXTENDED),
			this._extensionFraction
		));

		// Turn LEDs on or off
		let morsePhase = Math.floor(time / Station.MORSE_RHYTHM)
						% this._morseName.length;
		this._morseLed.visible = (this._morseName[morsePhase] === "1");

		let actionPhase = Math.floor(time / Station.ACTION_RHYTHM)
						% 2;
		let somethingToDo = this.space.game.unicenter.upgrader.anythingToDo();
		this._actionLed.visible = somethingToDo && (actionPhase === 0);
	}

	// Raise the station's shield
	// This function is called by the interactor whenever something is foolish
	// enough to attack a station.
	raiseShield() {
		this._shield.alpha = 0.2;
	}

	// Return a [pos, rot] of the docking location for the player
	// This location changes over time.
	getDockLocation() {
		let pos = Vector.rotateBy(
			this._texturePixelsToFlares(Station.DOCK_EXTENDED),
			this.rot + Math.PI / 2
		).add(this.pos);
		let rot = this.rot;

		return [pos, rot];
	}

	// Save this station into a config object
	// See the comment on Thing.save() for more details.
	save() {
		let config = super.save().config;

		config.name = this.name;
		config.extensionFraction = this._extensionFraction;

		return {
			config:     config,
			isComplete: true
		};
	}

	// Convert from texture pixels on this._body to flares used by this.display
	// tp is a vector in texture pixels
	_texturePixelsToFlares(tp) {
		return new Vector(- this._body.width / 2, - this._body.height / 2)
			.add(Vector.scale(tp, this._body.scale.x))
	}

	// Convert str to morse code represented as a sequence of 0s and 1s
	// For example "AB" -> "1011100011101010100000000000". 11 zeros at the end
	// are for easy looping. This function ignores unknown characters.
	static _morsify(str) {
		const SPACE = " ";

		let knownChars = new Set(Station.MORSE_CODE.keys());
		knownChars.add(SPACE);

		let result = "";

		for (let char of str.toUpperCase()) {
			// Ignore unknown characters
			if (!knownChars.has(char)) continue;

			if (char === SPACE) {
				// Add a gap for space
				result += "0".repeat(7);
			} else {
				// Add a gap between non-space characters
				if (result.length > 0 && result[result.length - 1] === "1") {
					result += "0".repeat(3);
				}
				// Convert dots and dashes to 0s and 1s
				result += Station.MORSE_CODE.get(char).split("")
					.map(symbol => (symbol === "-") ? "1".repeat(3) : "1")
					.join("0");
			}
		}

		// Add a gap at the end for easy looping.
		result += "0".repeat(11);

		return result;
	}
}
